import { useState, useRef, useEffect } from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';

export default function KlantZoeker({ klanten, klantId, onChange }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState({});
  const ref = useRef(null);
  const selected = klanten.find(k => k.id === klantId);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Keep the fixed popup inside the viewport, including while resizing or scrolling.
  useEffect(() => {
    if (!open || !ref.current) return;
    const place = () => {
      const rect = ref.current.getBoundingClientRect();
      const width = Math.min(rect.width, window.innerWidth - 32);
      setPopupStyle({ width, left: Math.max(16, Math.min(rect.left, window.innerWidth - width - 16)), top: rect.bottom });
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(ref.current);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const filtered = q ? klanten.filter(k =>
    `${k.voorn} ${k.achtern} ${k.bedrijf} ${k.email} ${k.btwnr}`.toLowerCase().includes(q.toLowerCase())
  ) : klanten;

  return (
    <div className="relative min-w-0 max-w-full" ref={ref}>
      <div className="flex items-center gap-2 px-3 py-2 min-w-0" style={{ background: '#F0F4FF', border: '1px solid #DBEAFE' }}>
        <MagnifyingGlass size={14} className="text-[#002FA7] shrink-0" />
        {selected && !open ? (
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0 cursor-pointer" onClick={() => { setOpen(true); setQ(''); }} title="Klik om een andere klant te kiezen" data-testid="klant-selected">
            <span className={`text-sm font-semibold text-[#002FA7] min-w-0 break-words [overflow-wrap:anywhere] ${selected.geblokkeerd ? 'klant-geblokkeerd-naam' : ''}`}>{selected.bedrijf || `${selected.voorn} ${selected.achtern}`}</span>
            <span className="text-[10px] text-gray-400 break-all">{selected.btwnr}</span>
            <button type="button" className="ml-auto text-[10px] font-bold text-amber-700 hover:underline whitespace-nowrap" onClick={(e) => { e.stopPropagation(); setOpen(true); setQ(''); }} data-testid="klant-wijzig">✎ Wijzig</button>
          </div>
        ) : (
          <input className="bg-transparent text-sm font-medium text-[#002FA7] outline-none flex-1 min-w-0 w-full" style={{ border: 'none' }}
            placeholder="Zoek klant (naam, bedrijf, BTW)..."
            value={q} onChange={e => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            autoFocus={open}
            data-testid="klant-zoek-input" />
        )}
        {selected && <button type="button" data-testid="klant-selectie-wissen" aria-label="Klantselectie wissen" className="text-gray-400 hover:text-red-500 text-xs shrink-0" onClick={() => { onChange(''); setQ(''); setOpen(true); }}>x</button>}
      </div>
      {open && (
        <div className="fixed bg-white border border-gray-200 shadow-xl max-h-60 overflow-y-auto" style={{ zIndex: 9999, ...popupStyle, maxWidth: 'calc(100vw - 32px)' }} data-testid="klant-search-results">
          {filtered.map(k => (
            <div key={k.id} className={`flex flex-wrap items-center justify-between gap-1 px-3 py-2 hover:bg-[#F0F4FF] cursor-pointer border-b border-gray-50 text-sm ${k.geblokkeerd ? 'bg-red-50/40' : ''}`}
              onClick={() => { onChange(k.id); setOpen(false); setQ(''); }} data-testid={`klant-result-${k.id}`}
              title={k.geblokkeerd ? `Geblokkeerd${k.geblokkeerd_reden ? ': ' + k.geblokkeerd_reden : ''}` : undefined}>
              <div className="min-w-0 max-w-full break-words [overflow-wrap:anywhere]">
                <div className={`font-semibold ${k.geblokkeerd ? 'klant-geblokkeerd-naam' : ''}`}>{k.bedrijf || `${k.voorn} ${k.achtern}`}{k.geblokkeerd && <span className="ml-1.5 text-[9px] font-bold text-red-600 no-underline" style={{ textDecoration: 'none', display: 'inline-block' }}>🔒 GEBLOKKEERD</span>}</div>
                <div className="text-[10px] text-gray-400">{k.adres}</div>
              </div>
              <div className="text-[10px] text-gray-400 min-w-0 break-all">{k.btwnr}</div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center text-gray-400 py-3 text-xs">Geen klanten gevonden</div>}
        </div>
      )}
      {selected && !open && (
        <div className="text-[10px] text-gray-500 mt-1 break-words [overflow-wrap:anywhere]" data-testid="klant-selected-details">{selected.adres}{selected.btwnr && ` | BTW: ${selected.btwnr}`}{selected.email && ` | ${selected.email}`}</div>
      )}
    </div>
  );
}
